/**
 * /contact -- public marketing-surface contact form.
 *
 * Lives under the `_public` shell (not behind auth) so a not-yet-
 * signed-up visitor can submit a support inquiry. The form posts to
 * /api/support/threads which detects the unauthenticated session and
 * routes the submission through SupportClient.ingestThreadMessage with
 * an anonymous profileId, so it lands in Clark with the same shape as
 * a logged-in user's inquiry.
 *
 * Distinct from the booking-page enquiry form (which spawns a workItem
 * for a specific tradesperson) -- this is for inquiries TO Manos
 * itself: questions, sales conversations, support before signup, etc.
 */

import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import type { Route } from "./+types/_public.contact";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CheckCircle, ArrowRight } from "@phosphor-icons/react";

// Map the optional ?intent= / ?topic= URL params to a sensible
// pre-filled subject. Keeps inbound enquiries pre-categorised so the
// support inbox routes faster. Unknown values fall through to an
// empty subject (user types their own).
const INTENT_SUBJECTS: Record<string, string> = {
  enterprise: "Enterprise enquiry",
  sales: "Sales question",
  support: "Support question",
};
const TOPIC_SUBJECTS: Record<string, string> = {
  privacy: "Privacy / data question",
  terms: "Terms of service question",
  cookies: "Cookies / tracking question",
  account: "Account help",
};

export const loader = async ({}: Route.LoaderArgs) => {
  // No session lookup needed; the page renders the same for everyone.
  // The form's POST target (/api/support/threads) handles the
  // anonymous-vs-authed branching server-side.
  return {};
};

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-fill subject from ?intent= or ?topic=. Runs once on mount;
  // we don't want it stomping the user's typing if they later
  // navigate within the SPA.
  useEffect(() => {
    const intent = searchParams.get("intent");
    const topic = searchParams.get("topic");
    const presetSubject =
      (intent && INTENT_SUBJECTS[intent]) ??
      (topic && TOPIC_SUBJECTS[topic]) ??
      "";
    if (presetSubject && !subject) {
      setSubject(presetSubject);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canSubmit =
    body.trim().length > 0 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) &&
    !submitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch("/api/support/threads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim() || undefined,
          email: email.trim(),
          phone: phone.trim() || undefined,
          subject: subject.trim() || undefined,
          bodyText: body.trim(),
          sourceLabel: "Manos public contact form",
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error ?? "request_failed");
      }
      setSubmitted(true);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message === "valid_email_required"
            ? "Please double-check the email address."
            : "Couldn't send your message. Please try again in a moment."
          : "Couldn't send your message. Please try again in a moment.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section className="py-16 lg:py-24">
        <div className="max-w-2xl mx-auto px-6 text-center space-y-6">
          <div className="h-12 w-12 rounded-full bg-emerald-100 dark:bg-emerald-500/20 mx-auto flex items-center justify-center">
            <CheckCircle weight="duotone" className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight">
            Thanks -- we got it.
          </h1>
          <p className="text-muted-foreground">
            We'll come back to you on the email you entered. If you
            haven't heard from us within a working day, send another
            message and we'll dig into what happened.
          </p>
          <div className="pt-4">
            <Button asChild className="bg-amber-600 hover:bg-amber-700 text-white">
              <Link to="/">
                Back to home
                <ArrowRight className="ml-2 h-4 w-4" weight="bold" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 lg:py-24">
      <div className="max-w-2xl mx-auto px-6 space-y-10">
        <header className="space-y-3">
          <h1 className="text-3xl lg:text-4xl font-extrabold tracking-tight">
            Talk to us
          </h1>
          <p className="text-muted-foreground">
            Questions about Manos, sales, technical support, or anything
            else -- send us a message and we'll get back to you.
          </p>
        </header>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="name">Your name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jane Smith"
                autoComplete="name"
                maxLength={120}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">
                Email <span className="text-rose-600">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                required
                maxLength={200}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone (optional)</Label>
            <Input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="07000 000000"
              autoComplete="tel"
              maxLength={40}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="subject">Subject (optional)</Label>
            <Input
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="A short headline"
              maxLength={120}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="body">
              Message <span className="text-rose-600">*</span>
            </Label>
            <Textarea
              id="body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={7}
              placeholder="Tell us what's on your mind..."
              required
              maxLength={6000}
            />
          </div>

          {error && (
            <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>
          )}

          <div className="flex items-center justify-end gap-3">
            <Button
              type="submit"
              disabled={!canSubmit}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              size="lg"
            >
              {submitting ? "Sending..." : "Send message"}
            </Button>
          </div>
        </form>

        <p className="text-xs text-muted-foreground border-t border-border pt-6">
          Already a Manos user? Sign in and use the Help &amp; Support
          page inside the app -- you'll be able to read replies and
          follow up there.
        </p>
      </div>
    </section>
  );
}
